import { useState, useEffect, useRef } from "react";

// ─────────────────────────────────────────
//  SCRATCH CELL — cheat-proof, two-layer
// ─────────────────────────────────────────
function ScratchCell({ label, revealed, correct, canScratch, onScratched, G, lang = "sv" }) {
  const scratchRef = useRef(null);   // top canvas: the scratchable cover
  const isDrawing = useRef(false);
  const [done, setDone] = useState(revealed);
  // done but not yet revealed = waiting for the server verdict (record_scratch)
  const verdictKnown = revealed;
  const sv = lang === "sv";

  // Keyboard path: Enter/Space "scratches" directly — same server round-trip
  // as the canvas, so it reveals nothing the pointer path doesn't
  function onKeyDown(e) {
    if ((e.key === "Enter" || e.key === " ") && canScratch && !done) {
      e.preventDefault();
      setDone(true);
      onScratched();
    }
  }
  const ariaLabel = done
    ? (verdictKnown
        ? (correct
            ? (sv ? `Alternativ ${label}: rätt svar` : `Option ${label}: correct answer`)
            : (sv ? `Alternativ ${label}: fel svar` : `Option ${label}: wrong answer`))
        : (sv ? `Alternativ ${label}: kontrollerar…` : `Option ${label}: checking…`))
    : canScratch
      ? (sv ? `Alternativ ${label} — tryck Enter för att skrapa` : `Option ${label} — press Enter to scratch`)
      : (sv ? `Alternativ ${label}` : `Option ${label}`);

  // Draw the opaque cover layer on mount / theme change
  useEffect(() => {
    const canvas = scratchRef.current;
    if (!canvas || done) return;
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = canScratch ? G.cardScratch : G.cardScratchDone;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Etiketten följer temat: vit på 22 % syntes knappt mot det ljusa lottpappret
    ctx.fillStyle = G.cardScratchLabel || "rgba(255,255,255,0.22)";
    ctx.font = "bold 17px 'Plus Jakarta Sans', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, canvas.width / 2, canvas.height / 2);
  }, [done, canScratch, label, G]);

  useEffect(() => { if (revealed && !done) setDone(true); }, [revealed]);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width, sy = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * sx, y: (src.clientY - rect.top) * sy };
  }

  function scratchAt(e) {
    if (!isDrawing.current || done) return;
    e.preventDefault();
    const canvas = scratchRef.current;
    const ctx = canvas.getContext("2d");

    // Erase the cover — but underneath is just the neutral card background, NOT the result
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(...Object.values(getPos(e, canvas)), 24, 0, Math.PI * 2);
    ctx.fill();

    // Count how much has been scratched
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) if (data[i] < 128) transparent++;
    const pct = transparent / (canvas.width * canvas.height);

    // Only reveal result once threshold is crossed
    if (pct > 0.55) {
      setDone(true);
      onScratched();
    }
  }

  function startScratch(e) {
    if (!canScratch || done) return;
    e.preventDefault();
    isDrawing.current = true;
    scratchAt(e);
  }
  function stopScratch() { isDrawing.current = false; }

  return (
    <div
      role="button"
      tabIndex={canScratch && !done ? 0 : -1}
      aria-label={ariaLabel}
      aria-disabled={!canScratch || done}
      onKeyDown={onKeyDown}
      className="scratch-cell"
      style={{ position: "relative", borderRadius: 12, aspectRatio: "1.4", overflow: "hidden", touchAction: "none" }}>

      {/* Bottom layer: neutral background while scratching; result only once
          the server has confirmed the attempt (verdictKnown) */}
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        background: done && verdictKnown ? (correct ? G.cardCorrectBg : G.cardWrongBg) : G.surface2,
        border: `1px solid ${done && verdictKnown ? (correct ? G.cardCorrectBorder : G.cardWrongBorder) : G.border}`,
        borderRadius: 12, transition: "background 0.3s, border-color 0.3s",
      }}>
        <span style={{ position: "absolute", top: 6, left: 10, fontSize: 10, color: G.muted, fontFamily: "'DM Mono',monospace" }}>{label}</span>
        {done && verdictKnown && correct && <span style={{ fontSize: 22, color: G.correct }}>✱</span>}
        {done && !verdictKnown && <span style={{ fontSize: 16, color: G.muted }}>…</span>}
      </div>

      {/* Top layer: scratchable canvas cover — removed from DOM when done */}
      {!done && (
        <canvas ref={scratchRef} width={160} height={110}
          // En ruta som inte går att skrapa (frågan klar, gruppen avslutad) har
          // ett täcke i samma ton som bakgrunden — dra in det en pixel så att
          // rutans ram syns, annars ser de oskrapade alternativen ramlösa ut.
          style={canScratch
            ? { position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: 12, cursor: "crosshair" }
            : { position: "absolute", inset: 1, width: "calc(100% - 2px)", height: "calc(100% - 2px)", borderRadius: 11, cursor: "default" }}
          onMouseDown={startScratch} onMouseMove={scratchAt} onMouseUp={stopScratch} onMouseLeave={stopScratch}
          onTouchStart={startScratch} onTouchMove={scratchAt} onTouchEnd={stopScratch}
        />
      )}
    </div>
  );
}

export { ScratchCell };
